import React, { useEffect, useState, useRef } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { BrainCircuitIcon, UsersIcon, TargetIcon, MailIcon, CheckCircleIcon, CalendarIcon, ShieldCheckIcon, FileTextIcon, BarChartIcon, ClockIcon, DatabaseIcon, LockIcon, ArrowRightIcon, ServerIcon, WrenchIcon, CloudIcon, InboxIcon, ZapIcon, ListCheckIcon, UserCircleIcon, MousePointerClickIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { AnimatedFeatureShowcase } from '../components/AnimatedFeatureShowcase';
export function Features() {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);
  const pageRef = useRef<HTMLDivElement>(null);
  const circuitPathRef = useRef<SVGPathElement>(null);
  const [pathLength, setPathLength] = useState(0);
  const [activeNodeIndex, setActiveNodeIndex] = useState(-1);
  // Define node positions (percentage of path)
  const nodePositions = [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9];
  // Handle scroll to update the dot position
  useEffect(() => {
    const handleScroll = () => {
      const position = window.scrollY;
      const maxScrollValue = document.body.scrollHeight - window.innerHeight;
      setScrollPosition(position);
      setMaxScroll(maxScrollValue);
      // Calculate which node is active based on scroll position
      const scrollPercentage = position / maxScrollValue;
      const closestNodeIndex = nodePositions.findIndex((pos, index) => {
        const nextPos = nodePositions[index + 1] || 1;
        return scrollPercentage >= pos - 0.05 && scrollPercentage < nextPos - 0.05;
      });
      setActiveNodeIndex(closestNodeIndex);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    handleScroll(); // Initial call
    // Calculate path length once the component is mounted
    if (circuitPathRef.current) {
      setPathLength(circuitPathRef.current.getTotalLength());
    }
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  // Calculate the position of the dot along the path
  const dotPosition = scrollPosition / maxScroll;
  const dotPoint = circuitPathRef.current && pathLength ? circuitPathRef.current.getPointAtLength(dotPosition * pathLength) : {
    x: 0,
    y: 0
  };
  return <div ref={pageRef} className="w-full min-h-screen bg-[#1E2A38] text-[#F5F7FA] font-sans relative" data-id="element-1836">
      {/* Subtle pulse glow at the top of the page */}
      <div className="absolute top-0 left-0 w-full overflow-hidden z-0 pointer-events-none" data-id="element-1837">
        <div className="absolute left-1/2 top-24 transform -translate-x-1/2 w-[400px] h-[400px] bg-[#C8F169] opacity-[0.05] rounded-full filter blur-3xl animate-pulse" data-id="element-1838"></div>
      </div>
      {/* Circuit Path SVG - Fixed position that follows sections */}
      <svg className="fixed top-0 left-0 w-full h-full pointer-events-none z-10" preserveAspectRatio="none" style={{
      opacity: 0.4
    }} data-id="element-1839">
        {/* Section-based circuit path */}
        <path ref={circuitPathRef} d="M150,0 C150,300 250,600 150,900 C50,1200 250,1500 150,1800 C50,2100 250,2400 150,3000" stroke="#C8F169" strokeWidth="2" fill="none" strokeDasharray="5,5" className="opacity-30" data-id="element-1840" />
        {/* Moving dot that follows the path */}
        <circle cx={dotPoint.x} cy={dotPoint.y} r="6" fill="#C8F169" className="animate-pulse" filter="drop-shadow(0 0 5px #C8F169)" data-id="element-1841" />
        {/* Connection nodes along the path */}
        {nodePositions.map((position, index) => {
        const nodePoint = circuitPathRef.current && pathLength ? circuitPathRef.current.getPointAtLength(position * pathLength) : {
          x: 0,
          y: 0
        };
        const isActive = index === activeNodeIndex;
        return <g key={index} data-id="element-1842">
              {/* Connection node */}
              <circle cx={nodePoint.x} cy={nodePoint.y} r={isActive ? '5' : '3'} fill="#C8F169" className={isActive ? 'opacity-100' : 'opacity-40'} filter={isActive ? 'drop-shadow(0 0 8px #C8F169)' : 'none'} data-id="element-1843" />
              {/* Horizontal connection line */}
              <line x1={nodePoint.x} y1={nodePoint.y} x2={nodePoint.x + (index % 2 === 0 ? 100 : -100)} y2={nodePoint.y} stroke="#C8F169" strokeWidth="2" strokeDasharray="5,5" className={isActive ? 'opacity-70' : 'opacity-30'} filter={isActive ? 'drop-shadow(0 0 3px #C8F169)' : 'none'} data-id="element-1844" />
              {/* Glow effect for active node */}
              {isActive && <circle cx={nodePoint.x} cy={nodePoint.y} r="12" fill="#C8F169" className="opacity-30 animate-pulse" data-id="element-1845" />}
            </g>;
      })}
      </svg>
      <Header data-id="element-1846" />
      <main data-id="element-1847">
        {/* Hero Section */}
        <section className="w-full py-16 md:py-24 px-6 md:px-12 flex flex-col items-center text-center relative z-20" data-id="element-1848">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 max-w-4xl" data-id="element-1849">
            Prep in minutes. Walk in like you spent hours.
          </h1>
          <p className="text-lg md:text-xl text-[#F5F7FA] text-opacity-80 max-w-2xl mb-10" data-id="element-1850">
            One AI brief, every meeting mastered.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl w-full mb-12" data-id="element-1851">
            <div className="bg-[#F5F7FA] bg-opacity-5 p-6 rounded-xl flex flex-col items-center text-center" data-id="element-1852">
              <div className="w-12 h-12 bg-[#C8F169] bg-opacity-20 rounded-lg flex items-center justify-center mb-4" data-id="element-1853">
                <TargetIcon className="h-6 w-6 text-[#C8F169]" data-id="element-1854" />
              </div>
              <h3 className="font-semibold mb-2" data-id="element-1855">
                Smarter, faster prep with AI
              </h3>
              <p className="text-sm text-[#F5F7FA] text-opacity-70" data-id="element-1856">
                Automate research and preparation with AI-powered insights
              </p>
            </div>
            <div className="bg-[#F5F7FA] bg-opacity-5 p-6 rounded-xl flex flex-col items-center text-center" data-id="element-1857">
              <div className="w-12 h-12 bg-[#C8F169] bg-opacity-20 rounded-lg flex items-center justify-center mb-4" data-id="element-1858">
                <BrainCircuitIcon className="h-6 w-6 text-[#C8F169]" data-id="element-1859" />
              </div>
              <h3 className="font-semibold mb-2" data-id="element-1860">
                Rich attendee and company insights
              </h3>
              <p className="text-sm text-[#F5F7FA] text-opacity-70" data-id="element-1861">
                Understand who you're meeting with and their business context
              </p>
            </div>
            <div className="bg-[#F5F7FA] bg-opacity-5 p-6 rounded-xl flex flex-col items-center text-center" data-id="element-1862">
              <div className="w-12 h-12 bg-[#C8F169] bg-opacity-20 rounded-lg flex items-center justify-center mb-4" data-id="element-1863">
                <UsersIcon className="h-6 w-6 text-[#C8F169]" data-id="element-1864" />
              </div>
              <h3 className="font-semibold mb-2" data-id="element-1865">
                Personalized objectives and talking points
              </h3>
              <p className="text-sm text-[#F5F7FA] text-opacity-70" data-id="element-1866">
                Never go into a meeting without a clear strategy
              </p>
            </div>
            <div className="bg-[#F5F7FA] bg-opacity-5 p-6 rounded-xl flex flex-col items-center text-center" data-id="element-1867">
              <div className="w-12 h-12 bg-[#C8F169] bg-opacity-20 rounded-lg flex items-center justify-center mb-4" data-id="element-1868">
                <MailIcon className="h-6 w-6 text-[#C8F169]" data-id="element-1869" />
              </div>
              <h3 className="font-semibold mb-2" data-id="element-1870">
                Follow-up and recap in one click
              </h3>
              <p className="text-sm text-[#F5F7FA] text-opacity-70" data-id="element-1871">
                Capture key takeaways and next steps automatically
              </p>
            </div>
          </div>
          <Button data-id="element-1872">See it in action</Button>
        </section>
        {/* Gmail Plugin Section */}
        <section className="w-full py-16 px-6 md:px-12 bg-[#1A2331]" data-id="element-1873">
          <div className="max-w-6xl mx-auto" data-id="element-1874">
            <div className="text-center mb-12" data-id="element-1875">
              <div className="inline-flex items-center px-4 py-1.5 bg-[#C8F169] bg-opacity-20 text-[#C8F169] rounded-full text-sm font-medium mb-6" data-id="element-1876">
                <InboxIcon className="w-4 h-4 mr-2" data-id="element-1877" />
                New Gmail Plugin
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1878">
                Prep for meetings right inside your inbox
              </h2>
              <p className="text-lg text-[#F5F7FA] text-opacity-80 max-w-3xl mx-auto" data-id="element-1879">
                Get AI-powered meeting briefs, attendee research, and business
                insights without leaving Gmail. Everything you need, where you
                need it.
              </p>
            </div>
            {/* Animated Feature Showcase */}
            <div className="relative mb-16" data-id="element-1880">
              <AnimatedFeatureShowcase data-id="element-1881" />
              {/* Feature callouts */}
              <div className="absolute top-[10%] left-[-5%] md:left-[-80px] bg-[#1E2A38] p-3 rounded-lg shadow-lg border border-[#F5F7FA] border-opacity-10 max-w-[200px] hidden md:block" data-id="element-1882">
                <div className="flex items-start" data-id="element-1883">
                  <div className="bg-[#C8F169] bg-opacity-20 p-2 rounded-lg mr-3" data-id="element-1884">
                    <CalendarIcon className="h-5 w-5 text-[#C8F169]" data-id="element-1885" />
                  </div>
                  <div data-id="element-1886">
                    <h4 className="font-medium text-sm" data-id="element-1887">Calendar Sync</h4>
                    <p className="text-xs text-[#F5F7FA] text-opacity-70" data-id="element-1888">
                      Auto-detects meetings from your calendar
                    </p>
                  </div>
                </div>
              </div>
              <div className="absolute top-[30%] right-[-5%] md:right-[-80px] bg-[#1E2A38] p-3 rounded-lg shadow-lg border border-[#F5F7FA] border-opacity-10 max-w-[200px] hidden md:block" data-id="element-1889">
                <div className="flex items-start" data-id="element-1890">
                  <div className="bg-[#C8F169] bg-opacity-20 p-2 rounded-lg mr-3" data-id="element-1891">
                    <UserCircleIcon className="h-5 w-5 text-[#C8F169]" data-id="element-1892" />
                  </div>
                  <div data-id="element-1893">
                    <h4 className="font-medium text-sm" data-id="element-1894">Attendee Profiles</h4>
                    <p className="text-xs text-[#F5F7FA] text-opacity-70" data-id="element-1895">
                      Instant LinkedIn and role context
                    </p>
                  </div>
                </div>
              </div>
              <div className="absolute bottom-[15%] left-[5%] md:left-[-60px] bg-[#1E2A38] p-3 rounded-lg shadow-lg border border-[#F5F7FA] border-opacity-10 max-w-[200px] hidden md:block" data-id="element-1896">
                <div className="flex items-start" data-id="element-1897">
                  <div className="bg-[#C8F169] bg-opacity-20 p-2 rounded-lg mr-3" data-id="element-1898">
                    <ListCheckIcon className="h-5 w-5 text-[#C8F169]" data-id="element-1899" />
                  </div>
                  <div data-id="element-1900">
                    <h4 className="font-medium text-sm" data-id="element-1901">Business Insights</h4>
                    <p className="text-xs text-[#F5F7FA] text-opacity-70" data-id="element-1902">
                      Key metrics and company context
                    </p>
                  </div>
                </div>
              </div>
              <div className="absolute bottom-[30%] right-[5%] md:right-[-70px] bg-[#1E2A38] p-3 rounded-lg shadow-lg border border-[#F5F7FA] border-opacity-10 max-w-[200px] hidden md:block" data-id="element-1903">
                <div className="flex items-start" data-id="element-1904">
                  <div className="bg-[#C8F169] bg-opacity-20 p-2 rounded-lg mr-3" data-id="element-1905">
                    <ZapIcon className="h-5 w-5 text-[#C8F169]" data-id="element-1906" />
                  </div>
                  <div data-id="element-1907">
                    <h4 className="font-medium text-sm" data-id="element-1908">One-Click Prep</h4>
                    <p className="text-xs text-[#F5F7FA] text-opacity-70" data-id="element-1909">
                      Generate briefs instantly
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1910">
              <div className="bg-[#1E2A38] p-6 rounded-xl" data-id="element-1911">
                <div className="w-12 h-12 bg-[#C8F169] bg-opacity-20 rounded-lg flex items-center justify-center mb-4" data-id="element-1912">
                  <MousePointerClickIcon className="h-6 w-6 text-[#C8F169]" data-id="element-1913" />
                </div>
                <h3 className="text-xl font-semibold mb-3" data-id="element-1914">
                  One-Click Installation
                </h3>
                <p className="text-[#F5F7FA] text-opacity-80" data-id="element-1915">
                  Install directly from the Google Workspace Marketplace with a
                  single click. No complex setup or configuration required.
                </p>
              </div>
              <div className="bg-[#1E2A38] p-6 rounded-xl" data-id="element-1916">
                <div className="w-12 h-12 bg-[#C8F169] bg-opacity-20 rounded-lg flex items-center justify-center mb-4" data-id="element-1917">
                  <ZapIcon className="h-6 w-6 text-[#C8F169]" data-id="element-1918" />
                </div>
                <h3 className="text-xl font-semibold mb-3" data-id="element-1919">
                  Works Where You Do
                </h3>
                <p className="text-[#F5F7FA] text-opacity-80" data-id="element-1920">
                  Access all Prepz.io features directly in Gmail. No more
                  switching between apps or browser tabs during your workday.
                </p>
              </div>
              <div className="bg-[#1E2A38] p-6 rounded-xl" data-id="element-1921">
                <div className="w-12 h-12 bg-[#C8F169] bg-opacity-20 rounded-lg flex items-center justify-center mb-4" data-id="element-1922">
                  <ShieldCheckIcon className="h-6 w-6 text-[#C8F169]" data-id="element-1923" />
                </div>
                <h3 className="text-xl font-semibold mb-3" data-id="element-1924">
                  Enterprise-Ready Security
                </h3>
                <p className="text-[#F5F7FA] text-opacity-80" data-id="element-1925">
                  SOC2-compliant with limited permissions. We only access what's
                  needed, keeping your sensitive data secure and private.
                </p>
              </div>
            </div>
            <div className="mt-12 text-center" data-id="element-1926">
              <Button data-id="element-1927">Install Gmail Plugin</Button>
              <p className="mt-4 text-sm text-[#F5F7FA] text-opacity-60" data-id="element-1928">
                Works with Google Workspace and Gmail for Business
              </p>
            </div>
          </div>
        </section>
        {/* AI-Powered Briefs Section */}
        <section className="w-full py-16 px-6 md:px-12" data-id="element-1929">
          <div className="max-w-6xl mx-auto" data-id="element-1930">
            <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-1931">
              <div className="md:w-1/2" data-id="element-1932">
                <div className="bg-[#1E2A38] rounded-xl overflow-hidden shadow-xl" data-id="element-1933">
                  <div className="p-6 border-b border-[#F5F7FA] border-opacity-10" data-id="element-1934">
                    <div className="flex items-center justify-between mb-6" data-id="element-1935">
                      <div className="flex items-center space-x-4" data-id="element-1936">
                        <div className="w-10 h-10 rounded-full bg-[#C8F169] flex items-center justify-center text-[#1E2A38] font-bold" data-id="element-1937">
                          WS
                        </div>
                        <div data-id="element-1938">
                          <h3 className="font-medium" data-id="element-1939">
                            Quarterly Strategy Meeting
                          </h3>
                          <p className="text-sm text-[#F5F7FA] text-opacity-60" data-id="element-1940">
                            Tomorrow, 10:00 AM - 11:30 AM
                          </p>
                        </div>
                      </div>
                      <div className="bg-[#C8F169] bg-opacity-20 px-3 py-1 rounded-full" data-id="element-1941">
                        <span className="text-sm text-[#C8F169]" data-id="element-1942">
                          Important
                        </span>
                      </div>
                    </div>
                    <div data-id="element-1943">
                      <h4 className="text-sm text-[#F5F7FA] text-opacity-60 mb-2" data-id="element-1944">
                        Meeting Brief
                      </h4>
                      <p className="text-[#F5F7FA]" data-id="element-1945">
                        This quarterly strategy meeting will focus on reviewing
                        Q3 results and planning for Q4 initiatives. The team
                        will discuss current project status, resource
                        allocation, and upcoming market opportunities.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="md:w-1/2" data-id="element-1946">
                <h2 className="text-3xl font-bold mb-6" data-id="element-1947">AI-Powered Briefs</h2>
                <p className="text-lg mb-6 text-[#F5F7FA] text-opacity-80" data-id="element-1948">
                  Say goodbye to hours of preparation. Our AI automatically
                  generates comprehensive meeting briefs with all the context
                  you need.
                </p>
                <ul className="space-y-4" data-id="element-1949">
                  <li className="flex items-start" data-id="element-1950">
                    <CheckCircleIcon className="h-6 w-6 text-[#C8F169] mr-3 mt-0.5 flex-shrink-0" data-id="element-1951" />
                    <span data-id="element-1952">Auto-generated summaries for every meeting</span>
                  </li>
                  <li className="flex items-start" data-id="element-1953">
                    <CheckCircleIcon className="h-6 w-6 text-[#C8F169] mr-3 mt-0.5 flex-shrink-0" data-id="element-1954" />
                    <span data-id="element-1955">
                      Includes company context, past notes, and meeting goals
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1956">
                    <CheckCircleIcon className="h-6 w-6 text-[#C8F169] mr-3 mt-0.5 flex-shrink-0" data-id="element-1957" />
                    <span data-id="element-1958">Save hours of Googling and guesswork</span>
                  </li>
                </ul>
                <div className="mt-8" data-id="element-1959">
                  <Button data-id="element-1960">See a sample AI brief</Button>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Attendee Intelligence Section */}
        <section className="w-full py-16 px-6 md:px-12 bg-[#1A2331]" data-id="element-1961">
          <div className="max-w-6xl mx-auto" data-id="element-1962">
            <div className="flex flex-col md:flex-row-reverse items-center gap-12" data-id="element-1963">
              <div className="md:w-1/2" data-id="element-1964">
                <div className="bg-[#F5F7FA] bg-opacity-5 rounded-xl overflow-hidden shadow-xl" data-id="element-1965">
                  <div className="p-6" data-id="element-1966">
                    <h4 className="text-sm text-[#F5F7FA] text-opacity-60 mb-4" data-id="element-1967">
                      Attendees (5)
                    </h4>
                    <div className="space-y-3" data-id="element-1968">
                      <div className="bg-[#1E2A38] p-4 rounded-lg" data-id="element-1969">
                        <div className="flex items-center justify-between" data-id="element-1970">
                          <div className="flex items-center space-x-3" data-id="element-1971">
                            <div className="w-10 h-10 rounded-full bg-[#C8F169] flex items-center justify-center text-[#1E2A38] text-xs font-bold" data-id="element-1972">
                              WS
                            </div>
                            <div data-id="element-1973">
                              <div className="font-medium" data-id="element-1974">William Smith</div>
                              <div className="text-sm text-[#F5F7FA] text-opacity-60" data-id="element-1975">
                                VP of Product, TechCorp
                              </div>
                            </div>
                          </div>
                          <a href="#" className="text-[#C8F169] flex items-center hover:underline" data-id="element-1976">
                            <span className="text-sm" data-id="element-1977">LinkedIn</span>
                          </a>
                        </div>
                        <div className="mt-3 text-sm text-[#F5F7FA] text-opacity-80 bg-[#F5F7FA] bg-opacity-5 p-3 rounded" data-id="element-1978">
                          <p data-id="element-1979">• At TechCorp since 2019</p>
                          <p data-id="element-1980">• Previously Director at InnovateCo</p>
                          <p data-id="element-1981">• 3 previous meetings, last contact 3 days ago</p>
                        </div>
                      </div>
                      <div className="bg-[#1E2A38] p-4 rounded-lg" data-id="element-1982">
                        <div className="flex items-center justify-between" data-id="element-1983">
                          <div className="flex items-center space-x-3" data-id="element-1984">
                            <div className="w-10 h-10 rounded-full bg-[#C8F169] flex items-center justify-center text-[#1E2A38] text-xs font-bold" data-id="element-1985">
                              AJ
                            </div>
                            <div data-id="element-1986">
                              <div className="font-medium" data-id="element-1987">Alice Johnson</div>
                              <div className="text-sm text-[#F5F7FA] text-opacity-60" data-id="element-1988">
                                Director of Marketing, TechCorp
                              </div>
                            </div>
                          </div>
                          <a href="#" className="text-[#C8F169] flex items-center hover:underline" data-id="element-1989">
                            <span className="text-sm" data-id="element-1990">LinkedIn</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="md:w-1/2" data-id="element-1991">
                <h2 className="text-3xl font-bold mb-6" data-id="element-1992">
                  Attendee Intelligence
                </h2>
                <p className="text-lg mb-6 text-[#F5F7FA] text-opacity-80" data-id="element-1993">
                  Never walk into a meeting blind. Get comprehensive profiles on
                  every participant and tailor your approach accordingly.
                </p>
                <ul className="space-y-4" data-id="element-1994">
                  <li className="flex items-start" data-id="element-1995">
                    <CheckCircleIcon className="h-6 w-6 text-[#C8F169] mr-3 mt-0.5 flex-shrink-0" data-id="element-1996" />
                    <span data-id="element-1997">
                      Name, role, LinkedIn, and organizational context
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1998">
                    <CheckCircleIcon className="h-6 w-6 text-[#C8F169] mr-3 mt-0.5 flex-shrink-0" data-id="element-1999" />
                    <span data-id="element-2000">
                      History of past interactions and key talking points
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2001">
                    <CheckCircleIcon className="h-6 w-6 text-[#C8F169] mr-3 mt-0.5 flex-shrink-0" data-id="element-2002" />
                    <span data-id="element-2003">
                      Optional enrichment via third-party data sources
                    </span>
                  </li>
                </ul>
                <div className="mt-8" data-id="element-2004">
                  <Button variant="secondary" data-id="element-2005">
                    Learn more about attendee insights
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Business Insights Section */}
        <section className="w-full py-16 px-6 md:px-12" data-id="element-2006">
          <div className="max-w-6xl mx-auto" data-id="element-2007">
            <div className="text-center mb-12" data-id="element-2008">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2009">
                Detailed Insights & Analysis
              </h2>
              <p className="text-lg text-[#F5F7FA] text-opacity-80 max-w-3xl mx-auto" data-id="element-2010">
                Get deeper context for every meeting with AI-powered business
                intelligence that helps you make informed decisions.
              </p>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-2011">
              <div className="md:w-1/2" data-id="element-2012">
                <div className="bg-[#1E2A38] rounded-xl overflow-hidden shadow-xl" data-id="element-2013">
                  <div className="p-6" data-id="element-2014">
                    <h4 className="text-sm text-[#F5F7FA] text-opacity-60 mb-4" data-id="element-2015">
                      Business Insights
                    </h4>
                    <div className="space-y-4" data-id="element-2016">
                      <div className="flex items-start space-x-3" data-id="element-2017">
                        <CheckCircleIcon className="h-5 w-5 text-[#C8F169] mt-0.5 flex-shrink-0" data-id="element-2018" />
                        <p className="text-[#F5F7FA]" data-id="element-2019">
                          Company grew 27% in Q3, exceeding the market average
                          of 18%
                        </p>
                      </div>
                      <div className="flex items-start space-x-3" data-id="element-2020">
                        <CheckCircleIcon className="h-5 w-5 text-[#C8F169] mt-0.5 flex-shrink-0" data-id="element-2021" />
                        <p className="text-[#F5F7FA]" data-id="element-2022">
                          New product launch scheduled for early Q4 with 85%
                          readiness
                        </p>
                      </div>
                      <div className="flex items-start space-x-3" data-id="element-2023">
                        <CheckCircleIcon className="h-5 w-5 text-[#C8F169] mt-0.5 flex-shrink-0" data-id="element-2024" />
                        <p className="text-[#F5F7FA]" data-id="element-2025">
                          Customer retention improved by 12% following the last
                          initiative
                        </p>
                      </div>
                    </div>
                    <div className="mt-6" data-id="element-2026">
                      <h4 className="text-sm text-[#F5F7FA] text-opacity-60 mb-4" data-id="element-2027">
                        Previous Engagement
                      </h4>
                      <div className="bg-[#F5F7FA] bg-opacity-5 p-4 rounded-lg" data-id="element-2028">
                        <div className="flex items-start space-x-3" data-id="element-2029">
                          <MailIcon className="h-5 w-5 text-[#C8F169] mt-0.5 flex-shrink-0" data-id="element-2030" />
                          <div data-id="element-2031">
                            <div className="text-sm font-medium mb-1" data-id="element-2032">
                              Q3 Results Discussion with William Smith
                            </div>
                            <p className="text-sm text-[#F5F7FA] text-opacity-80" data-id="element-2033">
                              William shared the Q3 performance metrics,
                              highlighting a 27% growth in user acquisition. He
                              requested preparation for questions about the
                              marketing budget allocation for Q4.
                            </p>
                            <div className="text-xs text-[#F5F7FA] text-opacity-60 mt-1" data-id="element-2034">
                              3 days ago
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="md:w-1/2" data-id="element-2035">
                <h3 className="text-2xl font-bold mb-6" data-id="element-2036">
                  Smart Objectives & Talking Points
                </h3>
                <p className="text-lg mb-6 text-[#F5F7FA] text-opacity-80" data-id="element-2037">
                  Our AI suggests meeting goals and talking points based on
                  previous communications, CRM data, and meeting context.
                </p>
                <ul className="space-y-4" data-id="element-2038">
                  <li className="flex items-start" data-id="element-2039">
                    <CheckCircleIcon className="h-6 w-6 text-[#C8F169] mr-3 mt-0.5 flex-shrink-0" data-id="element-2040" />
                    <span data-id="element-2041">
                      AI-generated meeting objectives tailored to your goals
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2042">
                    <CheckCircleIcon className="h-6 w-6 text-[#C8F169] mr-3 mt-0.5 flex-shrink-0" data-id="element-2043" />
                    <span data-id="element-2044">Real-time suggestions you can customize</span>
                  </li>
                  <li className="flex items-start" data-id="element-2045">
                    <CheckCircleIcon className="h-6 w-6 text-[#C8F169] mr-3 mt-0.5 flex-shrink-0" data-id="element-2046" />
                    <span data-id="element-2047">Better calls, fewer "wing-it" moments</span>
                  </li>
                </ul>
                <div className="mt-8" data-id="element-2048">
                  <Button data-id="element-2049">Try it for free</Button>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Key Features Grid */}
        <section className="w-full py-16 px-6 md:px-12 bg-[#1A2331]" data-id="element-2050">
          <div className="max-w-6xl mx-auto" data-id="element-2051">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-2052">
              Everything You Need for Meeting Success
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2053">
              <div className="bg-[#F5F7FA] bg-opacity-5 p-6 rounded-xl" data-id="element-2054">
                <div className="w-12 h-12 bg-[#C8F169] bg-opacity-20 rounded-lg flex items-center justify-center mb-4" data-id="element-2055">
                  <CalendarIcon className="h-6 w-6 text-[#C8F169]" data-id="element-2056" />
                </div>
                <h3 className="text-xl font-semibold mb-3" data-id="element-2057">
                  Calendar & Email Sync
                </h3>
                <p className="text-[#F5F7FA] text-opacity-80 mb-4" data-id="element-2058">
                  Connects to Google/Outlook and auto-pulls meetings and
                  attendees. No extra data entry — works where you already are.
                </p>
                <a href="#" className="text-[#C8F169] flex items-center hover:underline text-sm" data-id="element-2059">
                  Learn more <ArrowRightIcon className="ml-1 h-4 w-4" data-id="element-2060" />
                </a>
              </div>
              <div className="bg-[#F5F7FA] bg-opacity-5 p-6 rounded-xl" data-id="element-2061">
                <div className="w-12 h-12 bg-[#C8F169] bg-opacity-20 rounded-lg flex items-center justify-center mb-4" data-id="element-2062">
                  <FileTextIcon className="h-6 w-6 text-[#C8F169]" data-id="element-2063" />
                </div>
                <h3 className="text-xl font-semibold mb-3" data-id="element-2064">
                  Instant Post-Meeting Recaps
                </h3>
                <p className="text-[#F5F7FA] text-opacity-80 mb-4" data-id="element-2065">
                  One-click summaries + follow-up suggestions. Automatically
                  track action items to boost follow-through and clarity.
                </p>
                <a href="#" className="text-[#C8F169] flex items-center hover:underline text-sm" data-id="element-2066">
                  Learn more <ArrowRightIcon className="ml-1 h-4 w-4" data-id="element-2067" />
                </a>
              </div>
              <div className="bg-[#F5F7FA] bg-opacity-5 p-6 rounded-xl" data-id="element-2068">
                <div className="w-12 h-12 bg-[#C8F169] bg-opacity-20 rounded-lg flex items-center justify-center mb-4" data-id="element-2069">
                  <UsersIcon className="h-6 w-6 text-[#C8F169]" data-id="element-2070" />
                </div>
                <h3 className="text-xl font-semibold mb-3" data-id="element-2071">
                  Team Collaboration
                </h3>
                <p className="text-[#F5F7FA] text-opacity-80 mb-4" data-id="element-2072">
                  Shared prep, collective notes, and insights reuse. Performance
                  dashboards to see prep consistency across your team.
                </p>
                <a href="#" className="text-[#C8F169] flex items-center hover:underline text-sm" data-id="element-2073">
                  Learn more <ArrowRightIcon className="ml-1 h-4 w-4" data-id="element-2074" />
                </a>
              </div>
              <div className="bg-[#F5F7FA] bg-opacity-5 p-6 rounded-xl" data-id="element-2075">
                <div className="w-12 h-12 bg-[#C8F169] bg-opacity-20 rounded-lg flex items-center justify-center mb-4" data-id="element-2076">
                  <BarChartIcon className="h-6 w-6 text-[#C8F169]" data-id="element-2077" />
                </div>
                <h3 className="text-xl font-semibold mb-3" data-id="element-2078">
                  Performance Analytics
                </h3>
                <p className="text-[#F5F7FA] text-opacity-80 mb-4" data-id="element-2079">
                  Track meeting effectiveness, preparation time saved, and
                  outcome improvements with detailed analytics.
                </p>
                <a href="#" className="text-[#F5F7FA] flex items-center hover:underline text-sm" data-id="element-2080">
                  Learn more <ArrowRightIcon className="ml-1 h-4 w-4" data-id="element-2081" />
                </a>
              </div>
              <div className="bg-[#F5F7FA] bg-opacity-5 p-6 rounded-xl" data-id="element-2082">
                <div className="w-12 h-12 bg-[#C8F169] bg-opacity-20 rounded-lg flex items-center justify-center mb-4" data-id="element-2083">
                  <ClockIcon className="h-6 w-6 text-[#C8F169]" data-id="element-2084" />
                </div>
                <h3 className="text-xl font-semibold mb-3" data-id="element-2085">
                  Time-Saving Automation
                </h3>
                <p className="text-[#F5F7FA] text-opacity-80 mb-4" data-id="element-2086">
                  Automate routine meeting prep tasks and save hours every week
                  with smart workflows and templates.
                </p>
                <a href="#" className="text-[#C8F169] flex items-center hover:underline text-sm" data-id="element-2087">
                  Learn more <ArrowRightIcon className="ml-1 h-4 w-4" data-id="element-2088" />
                </a>
              </div>
              <div className="bg-[#F5F7FA] bg-opacity-5 p-6 rounded-xl" data-id="element-2089">
                <div className="w-12 h-12 bg-[#C8F169] bg-opacity-20 rounded-lg flex items-center justify-center mb-4" data-id="element-2090">
                  <ShieldCheckIcon className="h-6 w-6 text-[#C8F169]" data-id="element-2091" />
                </div>
                <h3 className="text-xl font-semibold mb-3" data-id="element-2092">
                  Security & Privacy
                </h3>
                <p className="text-[#F5F7FA] text-opacity-80 mb-4" data-id="element-2093">
                  SOC2-ready, GDPR compliant with clear boundaries on
                  email/calendar access. Enterprise-grade security for your
                  data.
                </p>
                <a href="#" className="text-[#C8F169] flex items-center hover:underline text-sm" data-id="element-2094">
                  Learn more <ArrowRightIcon className="ml-1 h-4 w-4" data-id="element-2095" />
                </a>
              </div>
            </div>
          </div>
        </section>
        {/* Bring Your Own LLM Section */}
        <section className="w-full py-16 px-6 md:px-12" data-id="element-2096">
          <div className="max-w-6xl mx-auto" data-id="element-2097">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center" data-id="element-2098">
              Bring Your Own LLM
            </h2>
            <p className="text-lg text-center text-[#F5F7FA] text-opacity-80 max-w-3xl mx-auto mb-16" data-id="element-2099">
              Enterprise-grade flexibility with model choice and data control.
              Use the AI that works best for your organization's needs.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2100">
              <div className="bg-[#1E2A38] p-8 rounded-xl" data-id="element-2101">
                <div className="w-14 h-14 bg-[#C8F169] bg-opacity-20 rounded-lg flex items-center justify-center mb-6" data-id="element-2102">
                  <LockIcon className="h-7 w-7 text-[#C8F169]" data-id="element-2103" />
                </div>
                <h3 className="text-2xl font-bold mb-4" data-id="element-2104">Trust & Control</h3>
                <p className="text-[#F5F7FA] text-opacity-80 mb-6" data-id="element-2105">
                  Your model, your data, your control. Bring your own LLM for
                  enhanced data privacy and compliance. Prepz.io can run on
                  OpenAI, Azure OpenAI, Anthropic, or any model you host —
                  ensuring sensitive information stays within your guardrails.
                </p>
                <ul className="space-y-3" data-id="element-2106">
                  <li className="flex items-start" data-id="element-2107">
                    <CheckCircleIcon className="h-5 w-5 text-[#C8F169] mr-3 mt-0.5 flex-shrink-0" data-id="element-2108" />
                    <span className="text-[#F5F7FA] text-opacity-90" data-id="element-2109">
                      Full data sovereignty
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2110">
                    <CheckCircleIcon className="h-5 w-5 text-[#C8F169] mr-3 mt-0.5 flex-shrink-0" data-id="element-2111" />
                    <span className="text-[#F5F7FA] text-opacity-90" data-id="element-2112">
                      Enhanced compliance controls
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2113">
                    <CheckCircleIcon className="h-5 w-5 text-[#C8F169] mr-3 mt-0.5 flex-shrink-0" data-id="element-2114" />
                    <span className="text-[#F5F7FA] text-opacity-90" data-id="element-2115">
                      Custom data retention policies
                    </span>
                  </li>
                </ul>
              </div>
              <div className="bg-[#1E2A38] p-8 rounded-xl" data-id="element-2116">
                <div className="w-14 h-14 bg-[#C8F169] bg-opacity-20 rounded-lg flex items-center justify-center mb-6" data-id="element-2117">
                  <WrenchIcon className="h-7 w-7 text-[#C8F169]" data-id="element-2118" />
                </div>
                <h3 className="text-2xl font-bold mb-4" data-id="element-2119">Custom Intelligence</h3>
                <p className="text-[#F5F7FA] text-opacity-80 mb-6" data-id="element-2120">
                  Use the model that knows your business. Fine-tuned model on
                  your sales scripts? Want to use industry-specific terminology?
                  Prepz.io supports custom LLMs, letting you deliver more
                  relevant, tailored meeting insights.
                </p>
                <ul className="space-y-3" data-id="element-2121">
                  <li className="flex items-start" data-id="element-2122">
                    <CheckCircleIcon className="h-5 w-5 text-[#C8F169] mr-3 mt-0.5 flex-shrink-0" data-id="element-2123" />
                    <span className="text-[#F5F7FA] text-opacity-90" data-id="element-2124">
                      Domain-specific knowledge
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2125">
                    <CheckCircleIcon className="h-5 w-5 text-[#C8F169] mr-3 mt-0.5 flex-shrink-0" data-id="element-2126" />
                    <span className="text-[#F5F7FA] text-opacity-90" data-id="element-2127">
                      Specialized industry terminology
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2128">
                    <CheckCircleIcon className="h-5 w-5 text-[#C8F169] mr-3 mt-0.5 flex-shrink-0" data-id="element-2129" />
                    <span className="text-[#F5F7FA] text-opacity-90" data-id="element-2130">
                      Customized output formats
                    </span>
                  </li>
                </ul>
              </div>
              <div className="bg-[#1E2A38] p-8 rounded-xl" data-id="element-2131">
                <div className="w-14 h-14 bg-[#C8F169] bg-opacity-20 rounded-lg flex items-center justify-center mb-6" data-id="element-2132">
                  <CloudIcon className="h-7 w-7 text-[#C8F169]" data-id="element-2133" />
                </div>
                <h3 className="text-2xl font-bold mb-4" data-id="element-2134">
                  Flexible Infrastructure
                </h3>
                <p className="text-[#F5F7FA] text-opacity-80 mb-6" data-id="element-2135">
                  Private, public, or hybrid — you choose. Integrate with
                  self-hosted LLMs, enterprise-grade APIs (like Bedrock or
                  Vertex AI), or region-specific models to meet your legal or
                  performance needs.
                </p>
                <ul className="space-y-3" data-id="element-2136">
                  <li className="flex items-start" data-id="element-2137">
                    <CheckCircleIcon className="h-5 w-5 text-[#C8F169] mr-3 mt-0.5 flex-shrink-0" data-id="element-2138" />
                    <span className="text-[#F5F7FA] text-opacity-90" data-id="element-2139">
                      Multiple deployment options
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2140">
                    <CheckCircleIcon className="h-5 w-5 text-[#C8F169] mr-3 mt-0.5 flex-shrink-0" data-id="element-2141" />
                    <span className="text-[#F5F7FA] text-opacity-90" data-id="element-2142">
                      Region-specific compliance
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2143">
                    <CheckCircleIcon className="h-5 w-5 text-[#C8F169] mr-3 mt-0.5 flex-shrink-0" data-id="element-2144" />
                    <span className="text-[#F5F7FA] text-opacity-90" data-id="element-2145">
                      Enterprise API integrations
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-12 text-center" data-id="element-2146">
              <Button data-id="element-2147">Schedule a consultation</Button>
            </div>
          </div>
        </section>
        {/* CTA Section */}
        <section className="w-full py-16 px-6 md:px-12 bg-[#C8F169] bg-opacity-10" data-id="element-2148">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2149">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2150">
              Ready to transform your meeting experience?
            </h2>
            <p className="text-lg md:text-xl text-[#F5F7FA] text-opacity-80 mb-10 max-w-2xl mx-auto" data-id="element-2151">
              Join thousands of professionals who are saving time and making
              better impressions with Prepz.io.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2152">
              <Button data-id="element-2153">Get Started for Free</Button>
              <Button variant="secondary" data-id="element-2154">Schedule a Demo</Button>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-2155" />
    </div>;
}